import React, { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';

import './legal.css';
import { api, setCurrentLegalUser } from './lib/api';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NotificationBell } from './components/NotificationBell';
import { RequireRole } from './components/RequireRole';
import { Dashboard } from './pages/Dashboard';
import { CaseList } from './pages/CaseList';
import { CaseCreate } from './pages/CaseCreate';
import { CaseDetail } from './pages/CaseDetail';
import { Approvals } from './pages/Approvals';
import { Reports } from './pages/Reports';
import { Customers } from './pages/Customers';
import { CustomerDetail } from './pages/CustomerDetail';
import { LegalFirms } from './pages/LegalFirms';
import { AdminApprovalHierarchy } from './pages/AdminApprovalHierarchy';

const BASE = '/legal-cases';

/**
 * The legal module's entry point - the standalone Legal app's `App.tsx` and
 * `AppShell.tsx`, minus what reception already provides: the router, login,
 * session/idle handling, user administration and the left navigation. Its own
 * sidebar became the tab strip below; its routes are nested under
 * /legal-cases/* (App.js mounts this with a trailing /*).
 *
 * Nothing renders until GET /api/legal/me answers, because every page's
 * Management check reads `currentUser()` synchronously from that answer.
 *
 * `.legal-root` scopes the module's design tokens (legal.css) and must stay on
 * the outermost element.
 */
const Legal = () => {
    const [me, setMe] = useState(undefined); // undefined = loading, null = failed

    useEffect(() => {
        let alive = true;
        api.get('/me')
            .then((profile) => {
                if (!alive) return;
                setCurrentLegalUser(profile);
                setMe(profile);
            })
            .catch(() => alive && setMe(null));
        return () => {
            alive = false;
        };
    }, []);

    if (me === undefined) return <div className="legal-root"><div className="legal-state">Loading…</div></div>;
    if (me === null) {
        return (
            <div className="legal-root">
                <div className="legal-state" style={{ color: 'var(--st-risk)' }}>
                    Couldn't load the Legal module. Check that you have the Legal Cases card, then reload.
                </div>
            </div>
        );
    }

    const isManagement = me.roleName === 'MANAGEMENT';
    const tabs = [
        { to: BASE, label: 'Dashboard', end: true },
        { to: `${BASE}/cases`, label: 'Cases' },
        { to: `${BASE}/approvals`, label: 'Approvals' },
        { to: `${BASE}/reports`, label: 'Reports' },
        { to: `${BASE}/customers`, label: 'Customers' },
        { to: `${BASE}/legal-firms`, label: 'Legal Firms' },
        ...(isManagement ? [{ to: `${BASE}/admin/approval-rules`, label: 'Approval Rules' }] : [])
    ];

    return (
        <div className="legal-root">
            <header className="legal-header">
                <div className="legal-header__title">
                    <strong>Legal Cases</strong>
                    <span>Case management</span>
                </div>
                <nav className="legal-tabs" aria-label="Legal module">
                    {tabs.map((t) => (
                        <NavLink key={t.to} to={t.to} end={t.end} className="legal-tab">
                            {t.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="legal-header__right">
                    <span className="legal-role-chip" title={isManagement ? 'You hold the approve action on Legal Cases' : 'You see the cases you are responsible for or created'}>
                        {isManagement ? 'Management' : 'Legal Coordinator'}
                    </span>
                    <NotificationBell />
                </div>
            </header>

            <main className="legal-main">
                <ErrorBoundary>
                    <Routes>
                        <Route index element={<Dashboard />} />
                        <Route path="cases" element={<CaseList />} />
                        <Route path="cases/new" element={<CaseCreate />} />
                        <Route path="cases/:id" element={<CaseDetail />} />
                        <Route path="approvals" element={<Approvals />} />
                        <Route path="reports" element={<Reports />} />
                        <Route path="customers" element={<Customers />} />
                        <Route path="customers/:id" element={<CustomerDetail />} />
                        <Route path="legal-firms" element={<LegalFirms />} />
                        <Route
                            path="admin/approval-rules"
                            element={(
                                <RequireRole roles={['MANAGEMENT']}>
                                    <AdminApprovalHierarchy />
                                </RequireRole>
                            )}
                        />
                        <Route path="*" element={<Navigate to={BASE} replace />} />
                    </Routes>
                </ErrorBoundary>
            </main>
        </div>
    );
};

export default Legal;
